/**
 * useUserPermissionsListener Hook
 *
 * Sets up a real-time Firestore listener on the user's permissions document.
 * When permissions change (admin grants/revokes access), the UI updates immediately.
 *
 * This is the SINGLE SOURCE OF TRUTH for user data in the application.
 * All user information (permissions, role, FCM tokens, notification settings)
 * flows through this listener into the Zustand store.
 *
 * Uses the generic useFirestoreDocument hook for consistency.
 *
 * Features:
 * - Real-time onSnapshot listener (not one-time getDoc)
 * - Updates Zustand store when permissions change
 * - Tracks loading state for initial render
 * - Invalidates localStorage cache automatically
 * - Auto-reconnects on network issues (Firebase SDK built-in)
 * - Cleanup on unmount
 * - Claims version synchronization: detects when Firestore claimsVersion is higher
 *   than the JWT token version and forces a token refresh for security rules
 *
 * Usage:
 *   // In AuthenticatedLayout after initial authentication
 *   useUserPermissionsListener(email, displayName, isConnected);
 */

import { useCallback, useEffect, useState } from 'react';
import { getApps, getApp } from 'firebase/app';
import { getAuth, getIdTokenResult } from 'firebase/auth';
import { useFirebaseStore } from '../store/firebaseStore';
import type { NotificationSettings, UIPreferences } from '../store/firebaseStore';
import { useFirestoreDocument } from './firestore';
import { invalidatePermissionCache } from './usePagePermissions';
import type { UserRole } from '../../types/rental.types';
import type { PageAccessConfig } from '../types/permissions.types';
import type { UserRoleAssignments } from '../types/roles.types';
import type { DocumentData } from 'firebase/firestore';
import type { FCMTokenEntry } from './useFCMToken';
import { logger } from '../utils/logger';

interface UserDocument {
  email: string;
  displayName?: string;
  role?: UserRole;
  pageAccess?: PageAccessConfig;  // Centralized permission structure (advanced mode)
  assignedRoles?: UserRoleAssignments;  // Role-based permissions (recommended mode)
  useAdvancedPermissions?: boolean;  // Permission mode flag
  defaultLocation?: string;
  claimsVersion?: number;  // Version for custom claims synchronization
  fcmTokens?: FCMTokenEntry[];  // FCM tokens for push notifications
  notificationSettings?: NotificationSettings;  // Notification preferences
  uiPreferences?: UIPreferences;  // UI preferences (header pinning, etc.)
  divisions?: string[];  // Division assignments
  quizManager?: boolean;  // Quiz manager switch (admin-set)
}

/**
 * Parse user document into UserDocument type
 */
function parseUserDocument(data: DocumentData): UserDocument {
  return {
    email: data.email || '',
    displayName: data.displayName,
    role: data.role,
    pageAccess: data.pageAccess,
    assignedRoles: data.assignedRoles,
    useAdvancedPermissions: data.useAdvancedPermissions,
    defaultLocation: data.defaultLocation,
    claimsVersion: data.claimsVersion,
    fcmTokens: data.fcmTokens,
    notificationSettings: data.notificationSettings,
    uiPreferences: data.uiPreferences,
    divisions: data.divisions,
    quizManager: data.quizManager === true,
  };
}

/**
 * Check if token needs refresh based on claimsVersion
 * Returns true if token was refreshed, false otherwise
 */
async function checkAndRefreshTokenIfNeeded(firestoreVersion: number | undefined): Promise<boolean> {
  try {
    // Get Firebase Auth instance
    if (getApps().length === 0) {
      return false;
    }

    const auth = getAuth(getApp());
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return false;
    }

    // Get current token claims
    const tokenResult = await getIdTokenResult(currentUser);
    const tokenVersion = (tokenResult.claims.claimsVersion as number) || 0;
    const firestoreVer = firestoreVersion || 0;

    // Check if Firestore has a newer version
    if (firestoreVer > tokenVersion) {
      logger.log(
        `[useUserPermissionsListener] Claims version mismatch: token=${tokenVersion}, firestore=${firestoreVer}. Refreshing token...`
      );

      // Force token refresh to get new claims
      await currentUser.getIdToken(true);

      logger.log('[useUserPermissionsListener] Token refreshed with updated permissions');
      return true;
    }

    return false;
  } catch (error) {
    logger.error('[useUserPermissionsListener] Error checking/refreshing token:', error);
    return false;
  }
}

/**
 * Setup real-time listener for user permission changes
 *
 * @param email - User email to listen for
 * @param _displayName - DEPRECATED: No longer used since user auto-creation was removed.
 *                       Kept for API compatibility. Users must be pre-created in Admin Panel.
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent false "not found" warnings before Firebase auth completes.
 */
export const useUserPermissionsListener = (
  email: string | null,
  _displayName: string | null = null,
  enabled: boolean = true
): void => {
  const db = useFirebaseStore((state) => state.db);
  const setCurrentUser = useFirebaseStore((state) => state.setCurrentUser);
  const setIsUserLoading = useFirebaseStore((state) => state.setIsUserLoading);

  // Track the current claimsVersion for token refresh detection
  // Using state (not ref) so changes trigger the useEffect
  const [claimsVersion, setClaimsVersion] = useState<number | undefined>(undefined);

  // Build document path
  const documentPath = email ? `users/${email}` : null;

  // Stable callback for handling loaded data
  // Note: Loading state is managed by onLoadingChange callback (no need to set here)
  const handleDataLoaded = useCallback(
    async (data: UserDocument | null) => {
      if (data && email) {
        // Update Zustand store with new permissions (includes fcmTokens, notificationSettings, uiPreferences)
        setCurrentUser({
          email: data.email || email,
          displayName: data.displayName || email,
          role: data.role || 'user',
          pagePermissions: data.pageAccess as PageAccessConfig,
          assignedRoles: data.assignedRoles,
          useAdvancedPermissions: data.useAdvancedPermissions,
          defaultLocation: data.defaultLocation,
          fcmTokens: data.fcmTokens,
          notificationSettings: data.notificationSettings,
          uiPreferences: data.uiPreferences,
          divisions: data.divisions,
          quizManager: data.quizManager === true,
        });

        // Invalidate localStorage cache to ensure usePagePermissions gets fresh data
        invalidatePermissionCache();

        // Update claimsVersion state to trigger token refresh check
        setClaimsVersion(data.claimsVersion);
      } else if (!data && email) {
        // This should not happen - backend now rejects unregistered users at login
        // Log error for debugging (possible race condition or manual deletion)
        logger.error(
          `[useUserPermissionsListener] User document not found for ${email}. ` +
          `User should have been rejected at login.`
        );

        // Set minimal user state (no permissions = effectively locked out)
        setCurrentUser({
          email,
          displayName: email,
          role: 'user',
          pagePermissions: {},
        });
      }
    },
    [email, setCurrentUser]
  );

  // Stable callback for handling errors
  // Note: Loading state is managed by onLoadingChange callback (no need to set here)
  const handleError = useCallback(
    (errorMessage: string | null) => {
      if (errorMessage) {
        logger.error(`[useUserPermissionsListener] Error listening to user document:`, errorMessage);
        // Note: Firebase SDK automatically retries on network errors
      }
    },
    []
  );

  // Stable callback for loading state changes
  // This ensures isUserLoading is set to true when listener re-subscribes (e.g., user switch)
  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setIsUserLoading(loading);
    },
    [setIsUserLoading]
  );

  // Use generic document listener hook
  // Only enable when both `enabled` prop is true AND we have required dependencies
  useFirestoreDocument<UserDocument>({
    db,
    documentPath,
    parseDocument: parseUserDocument,
    onDataLoaded: handleDataLoaded,
    onLoadingChange: handleLoadingChange,
    onError: handleError,
    enabled: enabled && !!email,
    // Disable metadata changes - we only care about actual data changes, not pending write states
    // This reduces snapshots from 2 to 1 after writes (no separate "server confirmed" callback)
    includeMetadataChanges: false,
  });

  // Check for claims version mismatch and refresh token if needed
  // This runs when claimsVersion changes in Firestore
  useEffect(() => {
    if (!email || claimsVersion === undefined) {
      return;
    }

    // Check if token needs refresh (async, fire-and-forget)
    // The refresh happens in the background and updates the token silently
    checkAndRefreshTokenIfNeeded(claimsVersion);
  }, [email, claimsVersion]);
};
